import { useMemo } from 'react';
import { SafeSvg, looksLikeSvg } from './SafeSvg';
import { cn } from '../lib/cn';

/**
 * Текст питання, у якому МОЖЕ трапитись малюнок.
 *
 * Потрібен усюди, де показується збережена умова: розбір помилок після сесії,
 * список помилок у кабінеті батьків, модерація банку. Ці екрани друкували
 * `prompt` звичайним текстом — і на питаннях із фігурами батько бачив не
 * задачу, а простирадло `<polygon points="…">`.
 *
 * Звідки там узагалі розмітка. Рушій колись клеїв фігури просто в текст
 * питання; це виправлено, але рядки, видані ДО виправлення, лишилися в базі
 * такими, як були, — історію спроб переписувати не можна. Тому розбір мусить
 * уміти показати і їх.
 *
 * Різати нічого не треба: розмітка тут не зайва, вона і є умова. Її досить
 * намалювати — тоді старе питання виглядає рівно так, як його бачила дитина.
 */

/** Умова, розкладена на шматки: що текст, а що малюнок. */
function split(text: string): Array<{ kind: 'text' | 'svg'; value: string }> {
  // Лінива квантифікація обовʼязкова: у ряду фігур кілька <svg> поспіль,
  // і жадібна забрала б їх усі в один шматок разом із текстом між ними.
  const parts = text.split(/(<svg[\s\S]*?<\/svg>)/g);
  return parts
    .filter((part) => part.trim() !== '')
    .map((part) => ({ kind: looksLikeSvg(part) ? ('svg' as const) : ('text' as const), value: part }));
}

/**
 * Ширина малюнка — з пропорції його ж viewBox.
 *
 * Через max-height це не робиться: `cn` — це clsx, а не tailwind-merge, тож
 * власний `[&>svg]:max-h-full` у `SafeSvg` і зовнішній `[&>svg]:max-h-40`
 * лишаються в класі обидва, і хто переможе — вирішує порядок правил у
 * стилях, а не намір. Наслідок був наочний: квадратна фігура 72×72 у розборі
 * розтяглася на всю панель заввишки з екран.
 *
 * Тому ціль задається ВИСОТОЮ, а ширина рахується з неї: смуга «продовж ряд»
 * (512×64) лишається широкою й низькою, а квадратна фігура — квадратною.
 */
const FIGURE_HEIGHT_PX = 96;

export function figureMaxWidth(markup: string): string | undefined {
  const box = /viewBox="([\d.\s-]+)"/i.exec(markup)?.[1]?.trim().split(/\s+/);
  if (!box || box.length < 4) return undefined;
  const width = Number(box[2]);
  const height = Number(box[3]);
  if (!Number.isFinite(width) || !Number.isFinite(height) || height <= 0) return undefined;
  return `${Math.round((width / height) * FIGURE_HEIGHT_PX)}px`;
}

export interface QuestionTextProps {
  text: string;
  className?: string;
  /** Підпис для екранного читача на кожному малюнку. */
  figureLabel?: string;
}

export function QuestionText({
  text,
  className,
  figureLabel = 'Малюнок до питання',
}: QuestionTextProps): React.JSX.Element {
  const parts = useMemo(() => split(text), [text]);

  // Найчастіший випадок — звичайний текст без жодної фігури. Тоді нічого не
  // обгортаємо: зайвий вузол зламав би наявну верстку заголовків.
  if (parts.length === 1 && parts[0]!.kind === 'text') {
    return <span className={className}>{text}</span>;
  }

  return (
    <span className={cn('flex flex-col gap-2', className)}>
      {parts.map((part, i) =>
        part.kind === 'svg' ? (
          <span key={i} className="mx-auto w-full" style={{ maxWidth: figureMaxWidth(part.value) }}>
            <SafeSvg markup={part.value} label={figureLabel} />
          </span>
        ) : (
          <span key={i}>{part.value.trim()}</span>
        ),
      )}
    </span>
  );
}

/** Чи є в умові вбудована розмітка — коли треба знати це поза рендером. */
export function hasEmbeddedFigure(text: string): boolean {
  return text.includes('<svg');
}
